/**
 * StackRankingSpace - Main interface for managing and viewing a stack ranking space
 * Includes: Items management, voting interface, results view
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Loader2, BarChart3, Users, Play, Pause,
  CheckCircle, Copy, Link2, Settings, Trash2, GripVertical,
  ChevronUp, ChevronDown, Edit, Save, X, RefreshCw,
  Download, FileText, ExternalLink, Award, Circle, Mail, Sparkles, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { stackRankingApi, epicsApi } from '../services/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

// Status config
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  active: { label: 'Voting Active', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Closed', color: 'bg-blue-100 text-blue-700' },
};

// Method labels
const METHOD_LABELS = {
  drag_drop: 'Drag & Drop',
  pairwise: 'Pairwise Comparison',
  dot_voting: 'Dot Voting',
  points_allocation: 'Points Allocation',
};

// Source type badges
const SOURCE_BADGES = {
  feature: { label: 'Feature', color: 'bg-blue-100 text-blue-700' },
  epic: { label: 'Epic', color: 'bg-indigo-100 text-indigo-700' },
  persona: { label: 'Persona', color: 'bg-pink-100 text-pink-700' },
  goal: { label: 'Goal', color: 'bg-amber-100 text-amber-700' },
  competitor: { label: 'Competitor', color: 'bg-red-100 text-red-700' },
  suggestion: { label: 'Suggestion', color: 'bg-purple-100 text-purple-700' },
  manual: { label: 'Manual', color: 'bg-gray-100 text-gray-600' },
};

const StackRankingSpace = () => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const { user } = useAuth();
  const colors = getThemeColors();

  const [loading, setLoading] = useState(true);
  const [space, setSpace] = useState(null);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('items');
  
  // Item management
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importSource, setImportSource] = useState('features');
  const [importing, setImporting] = useState(false);
  
  // Send to Team modal
  const [showSendToTeam, setShowSendToTeam] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sendingToTeam, setSendingToTeam] = useState(false);
  const [teamMessage, setTeamMessage] = useState('');
  
  // Results
  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  
  // Suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [epicsForSuggestions, setEpicsForSuggestions] = useState([]);

  // Vote Summary
  const [summary, setSummary] = useState(null);
  const [summaryHistory, setSummaryHistory] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummaryContent, setEditedSummaryContent] = useState('');
  const [showSummaryHistory, setShowSummaryHistory] = useState(false);

  // Load space and items
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [spaceRes, itemsRes] = await Promise.all([
        stackRankingApi.getSpace(spaceId),
        stackRankingApi.listItems(spaceId),
      ]);
      setSpace(spaceRes.data);
      // Items are now clean - suggestions are stored separately
      setItems(itemsRes.data);
      
      // Load suggestions separately if space allows them
      if (spaceRes.data.allow_suggestions) {
        loadSuggestions();
      }
    } catch (err) {
      console.error('Failed to load space:', err);
      toast.error('Failed to load stack ranking space');
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  // Load suggestions
  const loadSuggestions = async () => {
    try {
      setLoadingSuggestions(true);
      const res = await stackRankingApi.getSuggestions(spaceId);
      // Filter out dismissed and added suggestions - only show pending
      const pendingSuggestions = res.data.filter(s => s.status === 'pending');
      setSuggestions(pendingSuggestions);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load vote summary
  const loadSummary = async () => {
    try {
      setLoadingSummary(true);
      const [currentRes, historyRes] = await Promise.all([
        stackRankingApi.getCurrentSummary(spaceId),
        stackRankingApi.getSummaries(spaceId)
      ]);
      setSummary(currentRes.data);
      setSummaryHistory(historyRes.data || []);
      if (currentRes.data?.content) {
        setEditedSummaryContent(currentRes.data.content);
      }
    } catch (err) {
      console.error('Failed to load summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Load summary when tab changes to summary or when space status is closed
  useEffect(() => {
    if (activeTab === 'summary' && space?.status === 'closed') {
      loadSummary();
    }
  }, [activeTab, space?.status, spaceId]);

  // Handle save summary
  const handleSaveSummary = async () => {
    try {
      await stackRankingApi.updateSummary(spaceId, editedSummaryContent);
      toast.success('Summary saved');
      setIsEditingSummary(false);
      loadSummary(); // Refresh
    } catch (err) {
      toast.error('Failed to save summary');
    }
  };

  // Handle copy summary
  const handleCopySummary = () => {
    // Convert HTML to plain text for clipboard
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = summary?.content || '';
    const textContent = tempDiv.textContent || tempDiv.innerText;
    navigator.clipboard.writeText(textContent);
    toast.success('Summary copied to clipboard');
  };

  // Handle export PDF
  const handleExportPDF = () => {
    // Create a printable window with the summary
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Vote Summary - ${space?.name || 'Stack Ranking'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #333; border-bottom: 2px solid #6366f1; padding-bottom: 10px; }
          h3 { color: #4f46e5; margin-top: 24px; }
          ul, ol { margin-left: 20px; }
          li { margin-bottom: 8px; }
          .meta { color: #666; font-size: 14px; margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; }
        </style>
      </head>
      <body>
        <h1>Vote Summary: ${space?.name || 'Stack Ranking'}</h1>
        ${summary?.content || ''}
        <div class="meta">
          Generated: ${summary?.generated_at ? new Date(summary.generated_at).toLocaleDateString() : 'N/A'}
          ${summary?.last_edited_by_name ? ` • Edited by ${summary.last_edited_by_name}` : ''}
          • Participants: ${summary?.participant_count || 0}
          • Items Ranked: ${summary?.item_count || 0}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Load results
  const loadResults = async () => {
    try {
      setLoadingResults(true);
      const res = await stackRankingApi.getResults(spaceId);
      setResults(res.data);
    } catch (err) {
      console.error('Failed to load results:', err);
    } finally {
      setLoadingResults(false);
    }
  };

  // Load epics for suggestions dropdown
  useEffect(() => {
    const fetchEpics = async () => {
      if (space?.product_id) {
        try {
          const res = await epicsApi.listByProduct(space.product_id);
          setEpicsForSuggestions(res.data || []);
        } catch (err) {
          console.error('Failed to load epics:', err);
        }
      }
    };
    fetchEpics();
  }, [space?.product_id]);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoadingResults(true);
        const res = await stackRankingApi.getResults(spaceId);
        setResults(res.data);
      } catch (err) {
        console.error('Failed to load results:', err);
      } finally {
        setLoadingResults(false);
      }
    };
    
    if (activeTab === 'results') {
      fetchResults();
    }
  }, [activeTab, spaceId]);

  // Handle suggestion actions
  const handleSuggestionAction = async (suggestionId, action, epicId = null) => {
    try {
      const res = await stackRankingApi.handleSuggestion(spaceId, suggestionId, action, epicId);
      if (res.data.success) {
        if (action === 'add_to_epic') {
          toast.success(`Created feature in "${res.data.epic_title}"`);
        } else {
          toast.success('Suggestion dismissed');
        }
        // Refresh suggestions list
        loadSuggestions();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process suggestion');
    }
  };

  // Toggle status
  const toggleStatus = async (newStatus) => {
    try {
      await stackRankingApi.updateSpace(spaceId, { status: newStatus });
      setSpace(prev => ({ ...prev, status: newStatus }));
      toast.success(`Voting ${newStatus === 'active' ? 'started' : newStatus === 'closed' ? 'closed' : 'paused'}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Copy share link
  const copyShareLink = () => {
    const link = `${window.location.origin}/stack-ranking/vote/${space.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Share link copied!');
  };

  // Open share link in new window
  const openShareLink = () => {
    const link = `${window.location.origin}/stack-ranking/vote/${space.share_token}`;
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      link,
      'StackRankingVote',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  // Open Send to Team modal
  const openSendToTeam = async () => {
    try {
      const res = await stackRankingApi.getTeamMembers(spaceId);
      setTeamMembers(res.data);
      setSelectedMembers([]);
      setTeamMessage('');
      setShowSendToTeam(true);
    } catch (err) {
      toast.error('Failed to load team members');
    }
  };

  // Send invitations to selected team members
  const handleSendToTeam = async () => {
    if (selectedMembers.length === 0) {
      toast.error('Please select at least one team member');
      return;
    }
    
    setSendingToTeam(true);
    try {
      const res = await stackRankingApi.sendToTeam(spaceId, selectedMembers, teamMessage);
      toast.success(`Invitations sent to ${res.data.sent_count} team member(s)`);
      setShowSendToTeam(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setSendingToTeam(false);
    }
  };

  // Toggle team member selection
  const toggleMemberSelection = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Apply Rankings state
  const [showApplyRankings, setShowApplyRankings] = useState(false);
  const [applyRankingsCheck, setApplyRankingsCheck] = useState(null);
  const [checkingApply, setCheckingApply] = useState(false);
  const [applyingRankings, setApplyingRankings] = useState(false);

  // Check if apply rankings is available
  const checkApplyRankings = async () => {
    setCheckingApply(true);
    try {
      const res = await stackRankingApi.checkApplyRankings(spaceId);
      setApplyRankingsCheck(res.data);
      if (res.data.can_apply) {
        setShowApplyRankings(true);
      } else {
        toast.error(res.data.reason || 'Cannot apply rankings');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check rankings');
    } finally {
      setCheckingApply(false);
    }
  };

  // Apply rankings to features
  const handleApplyRankings = async () => {
    setApplyingRankings(true);
    try {
      const res = await stackRankingApi.applyRankings(spaceId);
      toast.success(res.data.message);
      setShowApplyRankings(false);
      
      // Navigate to releases tab with epic filter if provided
      if (res.data.redirect) {
        const { tab, epic } = res.data.redirect;
        const productId = space?.product_id;
        if (productId) {
          let url = `/products/${productId}?tab=${tab || 'releases'}`;
          if (epic && epic !== 'all') {
            url += `&epic=${epic}`;
          }
          navigate(url);
          return;
        }
      }
      
      // Fallback: refresh space data
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply rankings');
    } finally {
      setApplyingRankings(false);
    }
  };

  // Add item
  const handleAddItem = async () => {
    if (!newItemTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setAddingItem(true);
    try {
      const res = await stackRankingApi.addItem(spaceId, {
        title: newItemTitle.trim(),
        description: newItemDescription.trim(),
        source_type: 'manual',
      });
      setItems(prev => [...prev, res.data]);
      setNewItemTitle('');
      setNewItemDescription('');
      setShowAddItem(false);
      toast.success('Item added');
    } catch (err) {
      toast.error('Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  // Delete item
  const handleDeleteItem = async (itemId) => {
    try {
      await stackRankingApi.deleteItem(spaceId, itemId);
      setItems(prev => prev.filter(i => i.item_id !== itemId));
      toast.success('Item deleted');
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  // Import items
  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await stackRankingApi.importItems(spaceId, importSource);
      if (res.data.count > 0) {
        setItems(prev => [...prev, ...res.data.items]);
        toast.success(`Imported ${res.data.count} items from ${importSource}`);
      } else {
        toast.info(`No ${importSource} found to import`);
      }
      setShowImportModal(false);
    } catch (err) {
      toast.error('Failed to import items');
    } finally {
      setImporting(false);
    }
  };

  // Save item edit
  const handleSaveItem = async (item) => {
    try {
      await stackRankingApi.updateItem(spaceId, item.item_id, {
        title: item.title,
        description: item.description,
      });
      setItems(prev => prev.map(i => i.item_id === item.item_id ? item : i));
      setEditingItem(null);
      toast.success('Item updated');
    } catch (err) {
      toast.error('Failed to update item');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-red-500 mb-4">Space not found</p>
        <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[space.status] || STATUS_CONFIG.draft;

  return (
    <div className={cn("min-h-screen flex flex-col", colors.bgPrimary)}>
      {/* Header - matches CollaborationCanvas style */}
      <div className={cn("px-4 py-3 border-b flex items-center justify-between", colors.bgSecondary, colors.borderPrimary)}>
        <div className="flex items-center gap-4">
          {/* SR Logo */}
          <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
            SR
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className={cn("text-lg font-semibold", colors.textPrimary)}>{space.name}</h1>
              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            </div>
            <p className={cn("text-xs", colors.textMuted)}>
              {METHOD_LABELS[space.ranking_method]} • {items.length} items • {space.participant_count || 0} votes
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Copy/Open Link - only show when voting is active */}
          {space.status === 'active' && (
            <>
              <Button variant="outline" size="sm" onClick={openSendToTeam}>
                <Mail className="w-4 h-4 mr-2" />
                Send to Team
              </Button>
              <Button variant="outline" size="sm" onClick={openShareLink}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Link
              </Button>
              <Button variant="outline" size="sm" onClick={copyShareLink}>
                <Link2 className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </>
          )}
          
          {/* Draft state - Start Voting button */}
          {space.status === 'draft' && items.length > 0 && (
            <Button onClick={() => toggleStatus('active')} className="bg-green-600 hover:bg-green-700">
              <Play className="w-4 h-4 mr-2" />
              Start Voting
            </Button>
          )}
          
          {/* Active state - Close Voting button */}
          {space.status === 'active' && (
            <Button onClick={() => toggleStatus('closed')} variant="outline">
              <CheckCircle className="w-4 h-4 mr-2" />
              Close Voting
            </Button>
          )}
          
          {/* Closed state - Reopen Voting button + Apply Rankings */}
          {space.status === 'closed' && (
            <>
              {/* Apply Rankings - only for feature rankings */}
              {space.item_source === 'features' && !space.rankings_applied && (
                <Button 
                  onClick={checkApplyRankings} 
                  disabled={checkingApply}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {checkingApply ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Apply Rankings to Features
                </Button>
              )}
              {space.item_source === 'features' && space.rankings_applied && (
                <Badge className="bg-green-100 text-green-700 border-green-300">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Rankings Applied
                </Badge>
              )}
              <Button onClick={() => toggleStatus('active')} variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
                <RefreshCw className="w-4 h-4 mr-2" />
                Reopen Voting
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content - Full width */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="items">Items ({items.length})</TabsTrigger>
              <TabsTrigger value="vote" disabled={items.length === 0}>
                Preview
              </TabsTrigger>
              <TabsTrigger value="results">Results</TabsTrigger>
              <TabsTrigger value="participants">Participants</TabsTrigger>
              {space?.allow_suggestions && (
                <TabsTrigger value="suggestions">
                  Suggestions {suggestions.length > 0 && `(${suggestions.length})`}
                </TabsTrigger>
              )}
              {space?.status === 'closed' && (
                <TabsTrigger value="summary">
                  Summary
                </TabsTrigger>
              )}
            </TabsList>

          {/* Items Tab */}
          <TabsContent value="items">
            <div className="space-y-4">
              {/* Actions */}
              {space.status === 'draft' && (
                <div className="flex items-center gap-2">
                  <Button onClick={() => setShowAddItem(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                  <Button variant="outline" onClick={() => setShowImportModal(true)}>
                    <Download className="w-4 h-4 mr-2" />
                    Import
                  </Button>
                </div>
              )}

              {/* Items List */}
              {items.length === 0 ? (
                <Card className={cn("p-8 text-center", colors.bgCard, colors.borderPrimary)}>
                  <FileText className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
                  <h3 className={cn("font-semibold mb-2", colors.textPrimary)}>No items yet</h3>
                  <p className={cn("text-sm mb-4", colors.textMuted)}>
                    Add items to rank or import from your product data
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <Button onClick={() => setShowAddItem(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </Button>
                    <Button variant="outline" onClick={() => setShowImportModal(true)}>
                      <Download className="w-4 h-4 mr-2" />
                      Import
                    </Button>
                  </div>
                </Card>
              ) : (
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <Card
                      key={item.item_id}
                      className={cn("p-4", colors.bgCard, colors.borderPrimary)}
                    >
                      {editingItem?.item_id === item.item_id ? (
                        <div className="space-y-3">
                          <Input
                            value={editingItem.title}
                            onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                            placeholder="Title"
                          />
                          <Textarea
                            value={editingItem.description}
                            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                            placeholder="Description"
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleSaveItem(editingItem)}>
                              <Save className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)}>
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-4">
                          <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium", colors.bgTertiary, colors.textSecondary)}>
                            {item.rank || index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className={cn("font-medium", colors.textPrimary)}>{item.title}</h4>
                              {item.source_type && SOURCE_BADGES[item.source_type] && (
                                <Badge className={SOURCE_BADGES[item.source_type].color}>
                                  {SOURCE_BADGES[item.source_type].label}
                                </Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className={cn("text-sm mt-1", colors.textMuted)}>{item.description}</p>
                            )}
                            {item.score > 0 && (
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className={colors.textMuted}>Score: {item.score.toFixed(1)}</span>
                                {item.total_votes > 0 && (
                                  <span className={colors.textMuted}>{item.total_votes} votes</span>
                                )}
                              </div>
                            )}
                          </div>
                          {space.status === 'draft' && (
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.item_id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Vote Tab */}
          <TabsContent value="vote">
            <VotingInterface
              space={space}
              items={items}
              colors={colors}
              onVoteComplete={() => {
                loadData();
                setActiveTab('results');
              }}
            />
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            <ResultsView
              space={space}
              spaceId={spaceId}
              results={results}
              loading={loadingResults}
              onRefresh={loadResults}
              onClearAll={async () => {
                await stackRankingApi.clearAllVotes(spaceId);
                toast.success('All votes cleared');
                loadResults();
              }}
              colors={colors}
            />
          </TabsContent>

          {/* Participants Tab */}
          <TabsContent value="participants">
            <ParticipantsView spaceId={spaceId} space={space} colors={colors} />
          </TabsContent>

          {/* Suggestions Tab */}
          {space?.allow_suggestions && (
            <TabsContent value="suggestions">
              <div className="space-y-4">
                <div className={cn("p-4 rounded-lg border", colors.bgInput, colors.borderPrimary)}>
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className={cn("font-medium", colors.textPrimary)}>Voter Suggestions</p>
                      <p className={cn("text-sm mt-1", colors.textMuted)}>
                        Review ideas submitted by voters. Add promising ones to your backlog, 
                        then prioritize in a new vote.
                      </p>
                    </div>
                  </div>
                </div>

                {loadingSuggestions ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                  </div>
                ) : suggestions.length === 0 ? (
                  <Card className={cn("p-8 text-center", colors.bgCard)}>
                    <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className={cn("font-medium", colors.textSecondary)}>No suggestions yet</p>
                    <p className={cn("text-sm mt-1", colors.textMuted)}>
                      Voters can suggest new items during voting sessions.
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {suggestions.map(suggestion => (
                      <Card key={suggestion.suggestion_id} className={cn("p-4", colors.bgCard)}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-purple-100 text-purple-700 text-xs">
                                Suggestion
                              </Badge>
                              <h4 className={cn("font-medium", colors.textPrimary)}>{suggestion.title}</h4>
                            </div>
                            {suggestion.description && (
                              <p className={cn("text-sm mt-1", colors.textMuted)}>{suggestion.description}</p>
                            )}
                            <p className={cn("text-xs mt-2", colors.textMuted)}>
                              Suggested by {suggestion.suggested_by_name || 'Anonymous'} • {new Date(suggestion.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                                  Add to Epic
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {epicsForSuggestions.length === 0 ? (
                                  <DropdownMenuItem disabled>No epics available</DropdownMenuItem>
                                ) : (
                                  epicsForSuggestions.map(epic => (
                                    <DropdownMenuItem
                                      key={epic.epic_id}
                                      onClick={() => handleSuggestionAction(suggestion.suggestion_id, 'add_to_epic', epic.epic_id)}
                                    >
                                      {epic.title || epic.name || 'Untitled Epic'}
                                    </DropdownMenuItem>
                                  ))
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleSuggestionAction(suggestion.suggestion_id, 'dismiss')}
                            >
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Summary Tab - Only visible when vote is closed */}
          {space?.status === 'closed' && (
            <TabsContent value="summary">
              <div className="space-y-4">
                {/* Header with actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-500" />
                    <h3 className={cn("font-semibold", colors.textPrimary)}>Vote Summary</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {summary && !isEditingSummary && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => {
                          setEditedSummaryContent(summary.content || '');
                          setIsEditingSummary(true);
                        }}>
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCopySummary}>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleExportPDF}>
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        {summaryHistory.length > 1 && (
                          <Button size="sm" variant="outline" onClick={() => setShowSummaryHistory(true)}>
                            <Clock className="w-4 h-4 mr-1" />
                            History ({summaryHistory.length})
                          </Button>
                        )}
                      </>
                    )}
                    {isEditingSummary && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setIsEditingSummary(false)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveSummary} className="bg-indigo-600 hover:bg-indigo-700">
                          Save
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Summary content */}
                <Card className={cn("p-6", colors.bgCard)}>
                  {loadingSummary ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                      <span className={cn("ml-3", colors.textMuted)}>Loading summary...</span>
                    </div>
                  ) : !summary ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className={cn("font-medium", colors.textSecondary)}>No summary yet</p>
                      <p className={cn("text-sm mt-1", colors.textMuted)}>
                        Summary is being generated...
                      </p>
                      <Button 
                        className="mt-4" 
                        onClick={loadSummary}
                        variant="outline"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  ) : summary.status === 'generating' ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                      <span className={cn("ml-3", colors.textMuted)}>AI is generating summary...</span>
                    </div>
                  ) : isEditingSummary ? (
                    <div className="space-y-4">
                      <RichTextEditor
                        content={editedSummaryContent}
                        onChange={setEditedSummaryContent}
                        placeholder="Write your vote summary..."
                        minHeight={400}
                      />
                    </div>
                  ) : (
                    <div 
                      className={cn(
                        "prose prose-sm max-w-none dark:prose-invert",
                        "prose-headings:font-semibold prose-headings:mt-6 prose-headings:mb-3",
                        "prose-h3:text-lg prose-h3:text-gray-900 dark:prose-h3:text-gray-100",
                        "prose-p:mb-4 prose-p:leading-relaxed prose-p:text-gray-700 dark:prose-p:text-gray-300",
                        "prose-ul:mb-4 prose-ul:space-y-2 prose-ol:mb-4 prose-ol:space-y-2",
                        "prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-li:leading-relaxed",
                        "prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-strong:font-semibold",
                        colors.textPrimary
                      )}
                      dangerouslySetInnerHTML={{ __html: summary.content || '<p>No content</p>' }}
                    />
                  )}
                </Card>

                {/* Summary metadata */}
                {summary && summary.status === 'complete' && (
                  <div className={cn("text-sm", colors.textMuted)}>
                    Generated {summary.generated_at ? new Date(summary.generated_at).toLocaleDateString() : 'N/A'}
                    {summary.last_edited_by_name && (
                      <span> • Edited by {summary.last_edited_by_name} on {new Date(summary.last_edited_at).toLocaleDateString()}</span>
                    )}
                    {summary.version > 1 && (
                      <span> • Version {summary.version}</span>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
        </div>
      </div>

      {/* Add Item Modal */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className={cn(colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className={colors.textPrimary}>Add Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>Title *</label>
              <Input
                placeholder="Enter item title"
                value={newItemTitle}
                onChange={(e) => setNewItemTitle(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>Description</label>
              <Textarea
                placeholder="Optional description"
                value={newItemDescription}
                onChange={(e) => setNewItemDescription(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={addingItem}>
              {addingItem ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className={cn(colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className={colors.textPrimary}>Import Items</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Import from</label>
            <Select value={importSource} onValueChange={setImportSource}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="features">Features</SelectItem>
                <SelectItem value="epics">Epics</SelectItem>
                <SelectItem value="personas">Personas</SelectItem>
                <SelectItem value="goals">Goals</SelectItem>
                <SelectItem value="competitors">Competitors</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={importing}>
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to Team Modal */}
      <Dialog open={showSendToTeam} onOpenChange={setShowSendToTeam}>
        <DialogContent className={cn(colors.bgCard, "max-w-lg")}>
          <DialogHeader>
            <DialogTitle className={colors.textPrimary}>Send to Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className={cn("text-sm", colors.textMuted)}>
              Select team members to invite to vote on &quot;{space?.name}&quot;
            </p>
            
            {/* Team members list */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {teamMembers.length === 0 ? (
                <p className={cn("text-sm text-center py-4", colors.textMuted)}>
                  No team members found
                </p>
              ) : (
                teamMembers.map(member => (
                  <div
                    key={member.user_id}
                    onClick={() => !member.has_voted && toggleMemberSelection(member.user_id)}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                      member.has_voted 
                        ? "opacity-50 cursor-not-allowed bg-gray-50"
                        : selectedMembers.includes(member.user_id)
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                          : cn(colors.bgSecondary, colors.borderPrimary, "hover:border-gray-400")
                    )}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                      "bg-blue-100 text-blue-600"
                    )}>
                      {member.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn("font-medium text-sm", colors.textPrimary)}>{member.name}</p>
                      <p className={cn("text-xs truncate", colors.textMuted)}>{member.email}</p>
                    </div>
                    {member.has_voted ? (
                      <Badge className="bg-green-100 text-green-700 text-xs">Voted</Badge>
                    ) : selectedMembers.includes(member.user_id) ? (
                      <CheckCircle className="w-5 h-5 text-blue-500" />
                    ) : null}
                  </div>
                ))
              )}
            </div>
            
            {/* Custom message */}
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>
                Add a message (optional)
              </label>
              <Textarea
                placeholder="Let them know why this ranking is important..."
                value={teamMessage}
                onChange={(e) => setTeamMessage(e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
            
            {selectedMembers.length > 0 && (
              <p className={cn("text-sm", colors.textMuted)}>
                {selectedMembers.length} team member{selectedMembers.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowSendToTeam(false)}>Cancel</Button>
            <Button 
              onClick={handleSendToTeam} 
              disabled={sendingToTeam || selectedMembers.length === 0}
            >
              {sendingToTeam ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Send {selectedMembers.length > 0 ? `(${selectedMembers.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Rankings Confirmation Modal */}
      <Dialog open={showApplyRankings} onOpenChange={setShowApplyRankings}>
        <DialogContent className={cn(colors.bgCard, "max-w-lg")}>
          <DialogHeader>
            <DialogTitle className={colors.textPrimary}>Apply Rankings to Features</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className={cn("text-sm", colors.textMuted)}>
              This will update the priority order of your parking lot features based on the voting results.
            </p>
            
            {applyRankingsCheck && (
              <div className={cn("p-4 rounded-lg", colors.bgSecondary)}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className={cn("text-xs uppercase tracking-wide", colors.textMuted)}>Ranked Features</p>
                    <p className={cn("text-2xl font-bold", colors.textPrimary)}>
                      {applyRankingsCheck.ranked_features_count}
                    </p>
                  </div>
                  <div>
                    <p className={cn("text-xs uppercase tracking-wide", colors.textMuted)}>Total Parking Lot</p>
                    <p className={cn("text-2xl font-bold", colors.textPrimary)}>
                      {applyRankingsCheck.total_parking_lot_features}
                    </p>
                  </div>
                </div>
                
                {/* Warning for unranked features */}
                {applyRankingsCheck.warning && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-2">
                      ⚠️ {applyRankingsCheck.unranked_features_count} feature(s) were not included
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      {applyRankingsCheck.warning_message}
                    </p>
                    {applyRankingsCheck.unranked_features?.length > 0 && (
                      <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                        <p className="font-medium">Unranked features:</p>
                        <ul className="list-disc ml-4 mt-1">
                          {applyRankingsCheck.unranked_features.slice(0, 5).map(f => (
                            <li key={f.feature_id}>{f.title}</li>
                          ))}
                          {applyRankingsCheck.unranked_features.length > 5 && (
                            <li>...and {applyRankingsCheck.unranked_features.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <div className={cn("p-3 rounded-lg border", colors.borderPrimary, colors.bgCard)}>
              <p className={cn("text-sm font-medium mb-2", colors.textPrimary)}>What will happen:</p>
              <ul className={cn("text-sm space-y-1", colors.textMuted)}>
                <li>• Ranked features will be reordered by their voting score</li>
                <li>• Features with higher votes will get higher priority</li>
                {applyRankingsCheck?.unranked_features_count > 0 && (
                  <li>• Unranked features will be pushed to lower priority</li>
                )}
              </ul>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowApplyRankings(false)}>Cancel</Button>
            <Button 
              onClick={handleApplyRankings} 
              disabled={applyingRankings}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {applyingRankings ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Apply Rankings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary History Modal */}
      <Dialog open={showSummaryHistory} onOpenChange={setShowSummaryHistory}>
        <DialogContent className={cn(colors.bgCard, "max-w-lg")}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
              <Clock className="w-5 h-5 text-indigo-500" />
              Summary History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4 max-h-96 overflow-y-auto">
            {summaryHistory.map((hist) => (
              <Card 
                key={hist.summary_id} 
                className={cn(
                  "p-4 cursor-pointer hover:ring-2 hover:ring-indigo-300",
                  colors.bgInput,
                  hist.is_current && "ring-2 ring-indigo-500"
                )}
                onClick={() => {
                  if (hist.is_current) {
                    setShowSummaryHistory(false);
                  } else {
                    // View historical summary (read-only)
                    const tempWindow = window.open('', '_blank');
                    tempWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Vote Summary v${hist.version} - ${space?.name || 'Stack Ranking'}</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                          h1 { color: #333; border-bottom: 2px solid #6366f1; padding-bottom: 10px; }
                          .archived { background: #fef3c7; padding: 10px; border-radius: 4px; margin-bottom: 20px; }
                        </style>
                      </head>
                      <body>
                        <div class="archived">⚠️ This is an archived summary (Version ${hist.version})</div>
                        <h1>Vote Summary: ${space?.name || 'Stack Ranking'}</h1>
                        ${hist.content || '<p>No content</p>'}
                        <div style="color: #666; font-size: 14px; margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px;">
                          Generated: ${hist.generated_at ? new Date(hist.generated_at).toLocaleDateString() : 'N/A'}
                          • Participants: ${hist.participant_count || 0}
                          • Items: ${hist.item_count || 0}
                        </div>
                      </body>
                      </html>
                    `);
                    tempWindow.document.close();
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={cn("font-medium", colors.textPrimary)}>
                        Version {hist.version}
                      </span>
                      {hist.is_current && (
                        <Badge className="bg-indigo-100 text-indigo-700 text-xs">Current</Badge>
                      )}
                    </div>
                    <p className={cn("text-sm mt-1", colors.textMuted)}>
                      {hist.participant_count} participants • {hist.item_count} items
                    </p>
                    <p className={cn("text-xs mt-1", colors.textMuted)}>
                      {hist.generated_at ? new Date(hist.generated_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <ChevronUp className={cn("w-5 h-5 rotate-90", colors.textMuted)} />
                </div>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSummaryHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== VOTING INTERFACE ====================
const VotingInterface = ({ space, items, colors, onVoteComplete }) => {
  // Preview mode - show how the voting interface looks without allowing actual voting
  const [previewRankedItems] = useState([...items]);
  
  // For preview of dot voting
  const [previewDots] = useState(
    items.reduce((acc, item, idx) => ({ ...acc, [item.item_id]: idx < 3 ? 2 - idx : 0 }), {})
  );
  
  // For preview of pairwise
  const [previewPair] = useState(items.length >= 2 ? [items[0], items[1]] : null);
  
  // Copy share link
  const copyShareLink = () => {
    const link = `${window.location.origin}/stack-ranking/vote/${space.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Share link copied!');
  };

  // Open share link
  const openShareLink = () => {
    const link = `${window.location.origin}/stack-ranking/vote/${space.share_token}`;
    window.open(link, '_blank');
  };

  if (items.length === 0) {
    return (
      <Card className={cn("p-8 text-center", colors.bgCard, colors.borderPrimary)}>
        <p className={colors.textMuted}>Add items to preview the voting interface</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Preview Notice */}
      <Card className={cn("p-4 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800")}>
        <div className="flex items-start gap-3">
          <ExternalLink className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-medium text-amber-800 dark:text-amber-200">Preview Mode</h3>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              This is a preview of how participants will see the voting interface. 
              {space.status === 'active' 
                ? ' Share the link below to collect votes.'
                : space.status === 'draft'
                  ? ' Start voting to generate a shareable link.'
                  : ' Voting is currently closed.'
              }
            </p>
            {space.status === 'active' && (
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" onClick={openShareLink} className="bg-amber-600 hover:bg-amber-700">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Voting Link
                </Button>
                <Button size="sm" variant="outline" onClick={copyShareLink} className="border-amber-400 text-amber-700">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Preview of voting method */}
      <Card className={cn("p-6", colors.bgCard, colors.borderPrimary)}>
        <h3 className={cn("font-semibold mb-4", colors.textPrimary)}>
          {METHOD_LABELS[space.ranking_method]} Preview
        </h3>
        
        {/* Pairwise Preview */}
        {space.ranking_method === 'pairwise' && previewPair && (
          <div className="space-y-4">
            <p className={cn("text-sm mb-4", colors.textMuted)}>
              Participants will compare items two at a time and select the more important one.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {previewPair.map((item) => (
                <div
                  key={item.item_id}
                  className={cn(
                    "p-4 rounded-lg border-2 border-dashed cursor-not-allowed opacity-70",
                    colors.borderPrimary
                  )}
                >
                  <h4 className={cn("font-medium mb-1", colors.textPrimary)}>{item.title}</h4>
                  {item.description && (
                    <p className={cn("text-sm line-clamp-2", colors.textMuted)}>{item.description}</p>
                  )}
                </div>
              ))}
            </div>
            <p className={cn("text-xs text-center", colors.textMuted)}>
              Click to select the winner (disabled in preview)
            </p>
          </div>
        )}

        {/* Drag & Drop Preview */}
        {space.ranking_method === 'drag_drop' && (
          <div className="space-y-2">
            <p className={cn("text-sm mb-4", colors.textMuted)}>
              Participants will drag items to rank them from most to least important.
            </p>
            {previewRankedItems.slice(0, 5).map((item, index) => (
              <div
                key={item.item_id}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border cursor-not-allowed opacity-70",
                  colors.bgSecondary, colors.borderPrimary
                )}
              >
                <GripVertical className={cn("w-5 h-5", colors.textMuted)} />
                <span className={cn("w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium", 
                  "bg-blue-100 text-blue-700"
                )}>
                  {index + 1}
                </span>
                <span className={cn("font-medium", colors.textPrimary)}>{item.title}</span>
              </div>
            ))}
            {previewRankedItems.length > 5 && (
              <p className={cn("text-xs text-center", colors.textMuted)}>
                + {previewRankedItems.length - 5} more items
              </p>
            )}
          </div>
        )}

        {/* Dot Voting Preview */}
        {space.ranking_method === 'dot_voting' && (
          <div className="space-y-3">
            <p className={cn("text-sm mb-4", colors.textMuted)}>
              Participants have {space.dots_per_person || 5} dots to allocate across items.
            </p>
            {items.slice(0, 4).map((item) => (
              <div
                key={item.item_id}
                className={cn("flex items-center justify-between p-3 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}
              >
                <span className={cn("font-medium", colors.textPrimary)}>{item.title}</span>
                <div className="flex items-center gap-1">
                  {[...Array(previewDots[item.item_id] || 0)].map((_, i) => (
                    <Circle key={i} className="w-4 h-4 fill-blue-500 text-blue-500" />
                  ))}
                  {[...Array(3 - (previewDots[item.item_id] || 0))].map((_, i) => (
                    <Circle key={i} className={cn("w-4 h-4", colors.textMuted)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Points Allocation Preview */}
        {space.ranking_method === 'points_allocation' && (
          <div className="space-y-3">
            <p className={cn("text-sm mb-4", colors.textMuted)}>
              Participants allocate {space.max_points || 100} points across items.
            </p>
            {items.slice(0, 4).map((item, idx) => (
              <div
                key={item.item_id}
                className={cn("flex items-center justify-between p-3 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}
              >
                <span className={cn("font-medium", colors.textPrimary)}>{item.title}</span>
                <div className={cn("w-16 px-2 py-1 text-center rounded border", colors.bgInput, colors.borderPrimary)}>
                  <span className={colors.textMuted}>{[40, 30, 20, 10][idx] || 0}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};


// ==================== RESULTS VIEW ====================
const ResultsView = ({ space, spaceId, results, loading, onRefresh, onClearAll, colors }) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [actionType, setActionType] = useState('create_features');
  const [availableEpics, setAvailableEpics] = useState([]);
  const [targetEpicId, setTargetEpicId] = useState('');
  const [newEpicTitle, setNewEpicTitle] = useState('');
  const [newEpicDescription, setNewEpicDescription] = useState('');
  const [actioning, setActioning] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdEntities, setCreatedEntities] = useState([]);
  
  // AI Summary state
  const [aiSummary, setAiSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  const handleClearAll = async () => {
    setClearing(true);
    try {
      await onClearAll();
      setShowClearConfirm(false);
    } finally {
      setClearing(false);
    }
  };

  // Generate AI Summary
  const generateAISummary = async () => {
    setSummarizing(true);
    try {
      const res = await stackRankingApi.summarizeResults(spaceId);
      setAiSummary(res.data);
      toast.success('AI summary generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setSummarizing(false);
    }
  };

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    );
  };

  // Select top N items
  const selectTopN = (n) => {
    if (results?.items) {
      const topIds = results.items.slice(0, n).map(i => i.item_id);
      setSelectedItems(topIds);
    }
  };

  // Open action modal
  const openActionModal = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select items to action');
      return;
    }
    try {
      const res = await stackRankingApi.getAvailableEpics(spaceId);
      setAvailableEpics(res.data);
    } catch (err) {
      console.error('Failed to load epics:', err);
    }
    setShowActionModal(true);
  };

  // Execute action
  const handleAction = async () => {
    setActioning(true);
    try {
      const payload = {
        item_ids: selectedItems,
        action_type: actionType,
      };
      
      if (actionType === 'add_to_epic') {
        if (!targetEpicId) {
          toast.error('Please select an epic');
          setActioning(false);
          return;
        }
        payload.target_epic_id = targetEpicId;
      } else if (actionType === 'create_epic') {
        if (!newEpicTitle.trim()) {
          toast.error('Please enter an epic title');
          setActioning(false);
          return;
        }
        payload.epic_title = newEpicTitle.trim();
        payload.epic_description = newEpicDescription.trim();
      }
      
      const res = await stackRankingApi.actionResults(spaceId, payload);
      setCreatedEntities(res.data.created || []);
      setShowActionModal(false);
      setShowSuccessModal(true);
      toast.success(res.data.message);
      
      // Reset state
      setSelectedItems([]);
      setActionType('create_features');
      setTargetEpicId('');
      setNewEpicTitle('');
      setNewEpicDescription('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create items');
    } finally {
      setActioning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
      </div>
    );
  }

  if (!results || !results.items || results.items.length === 0) {
    return (
      <Card className={cn("p-8 text-center", colors.bgCard, colors.borderPrimary)}>
        <BarChart3 className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
        <h3 className={cn("font-semibold mb-2", colors.textPrimary)}>No results yet</h3>
        <p className={cn("text-sm", colors.textMuted)}>
          Results will appear here once people start voting
        </p>
      </Card>
    );
  }

  const maxScore = Math.max(...results.items.map(i => i.score || 0), 1);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={cn("font-semibold", colors.textPrimary)}>Rankings</h3>
          <p className={cn("text-sm", colors.textMuted)}>
            Based on {results.participant_count || 0} vote{results.participant_count !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Quick select buttons */}
          {results.items.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Select Top
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => selectTopN(3)}>Top 3</DropdownMenuItem>
                <DropdownMenuItem onClick={() => selectTopN(5)}>Top 5</DropdownMenuItem>
                <DropdownMenuItem onClick={() => selectTopN(10)}>Top 10</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedItems(results.items.map(i => i.item_id))}>
                  All Items
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedItems([])}>Clear Selection</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {selectedItems.length > 0 && (
            <Button size="sm" onClick={openActionModal} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Create from Selection ({selectedItems.length})
            </Button>
          )}
          
          {/* AI Summary Button */}
          {results.participant_count > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={generateAISummary}
              disabled={summarizing}
              className="border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              {summarizing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              AI Summary
            </Button>
          )}
          
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {results.participant_count > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setShowClearConfirm(true)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Votes
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* AI Summary Card */}
      {aiSummary && (
        <Card className={cn("p-4 border-purple-200 bg-purple-50 dark:bg-purple-950/20", colors.borderPrimary)}>
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-purple-800 dark:text-purple-200">AI Analysis</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setAiSummary(null)}
                  className="h-6 w-6 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className={cn("text-sm prose prose-sm max-w-none", colors.textSecondary)}>
                {aiSummary.summary.split('\n').map((line, idx) => (
                  <p key={idx} className="mb-2">{line}</p>
                ))}
              </div>
              <p className={cn("text-xs mt-3", colors.textMuted)}>
                Generated from {aiSummary.metadata?.participant_count || 0} votes • {new Date(aiSummary.metadata?.generated_at).toLocaleTimeString()}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Clear confirmation dialog */}
      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear All Votes?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 py-4">
            This will permanently delete all votes and participant data for this ranking. 
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleClearAll} disabled={clearing}>
              {clearing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear All Votes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Results Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className={cn(colors.bgCard, "max-w-lg")}>
          <DialogHeader>
            <DialogTitle className={colors.textPrimary}>Create from Ranking Results</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className={cn("text-sm", colors.textMuted)}>
              Create features or epics from {selectedItems.length} selected item{selectedItems.length > 1 ? 's' : ''}.
            </p>
            
            {/* Action Type Selection */}
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>What would you like to create?</label>
              <Select value={actionType} onValueChange={setActionType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="create_features">Create Features (adds to Prioritized Backlog)</SelectItem>
                  <SelectItem value="create_epic">Create New Epic with Features</SelectItem>
                  <SelectItem value="add_to_epic">Add Features to Existing Epic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Epic selection for add_to_epic */}
            {actionType === 'add_to_epic' && (
              <div>
                <label className={cn("text-sm font-medium", colors.textSecondary)}>Select Epic</label>
                <Select value={targetEpicId} onValueChange={setTargetEpicId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose an epic..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableEpics.map(epic => (
                      <SelectItem key={epic.epic_id} value={epic.epic_id}>
                        {epic.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Epic name for create_epic */}
            {actionType === 'create_epic' && (
              <>
                <div>
                  <label className={cn("text-sm font-medium", colors.textSecondary)}>Epic Title *</label>
                  <Input
                    value={newEpicTitle}
                    onChange={(e) => setNewEpicTitle(e.target.value)}
                    placeholder="e.g., Q1 2025 Priorities"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className={cn("text-sm font-medium", colors.textSecondary)}>Epic Description</label>
                  <Textarea
                    value={newEpicDescription}
                    onChange={(e) => setNewEpicDescription(e.target.value)}
                    placeholder="Optional description..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </>
            )}
            
            {/* Preview */}
            <div className={cn("p-3 rounded-lg text-sm", colors.bgSecondary)}>
              <p className={cn("font-medium mb-2", colors.textPrimary)}>Items to be created:</p>
              <ul className={cn("space-y-1", colors.textMuted)}>
                {results?.items
                  .filter(i => selectedItems.includes(i.item_id))
                  .slice(0, 5)
                  .map((item, idx) => (
                    <li key={item.item_id}>#{idx + 1} {item.title}</li>
                  ))}
                {selectedItems.length > 5 && (
                  <li className="italic">...and {selectedItems.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowActionModal(false)}>Cancel</Button>
            <Button onClick={handleAction} disabled={actioning} className="bg-green-600 hover:bg-green-700">
              {actioning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className={cn(colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className={cn("text-green-600 flex items-center gap-2")}>
              <CheckCircle className="w-5 h-5" />
              Items Created Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className={cn("text-sm mb-3", colors.textMuted)}>
              Created {createdEntities.length} item{createdEntities.length > 1 ? 's' : ''} from your ranking results:
            </p>
            <ul className={cn("space-y-2 max-h-48 overflow-y-auto", colors.textPrimary)}>
              {createdEntities.map((entity, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className={entity.type === 'epic' ? 'bg-indigo-50 text-indigo-700' : 'bg-blue-50 text-blue-700'}>
                    {entity.type}
                  </Badge>
                  {entity.title}
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSuccessModal(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-2">
        {results.items.map((item, index) => (
          <Card 
            key={item.item_id} 
            className={cn(
              "p-4 cursor-pointer transition-all",
              colors.bgCard, 
              colors.borderPrimary,
              selectedItems.includes(item.item_id) && "ring-2 ring-green-500 bg-green-50 dark:bg-green-950/20"
            )}
            onClick={() => toggleItemSelection(item.item_id)}
          >
            <div className="flex items-center gap-4">
              {/* Checkbox */}
              <div className={cn(
                "w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
                selectedItems.includes(item.item_id) 
                  ? "bg-green-500 border-green-500 text-white" 
                  : cn(colors.borderPrimary)
              )}>
                {selectedItems.includes(item.item_id) && <CheckCircle className="w-4 h-4" />}
              </div>
              
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center font-bold",
                index === 0 ? "bg-yellow-100 text-yellow-700" :
                index === 1 ? "bg-gray-100 text-gray-700" :
                index === 2 ? "bg-orange-100 text-orange-700" :
                "bg-gray-50 text-gray-500"
              )}>
                {index === 0 && <Award className="w-5 h-5" />}
                {index !== 0 && (index + 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className={cn("font-medium", colors.textPrimary)}>{item.title}</h4>
                  {item.source_type && SOURCE_BADGES[item.source_type] && (
                    <Badge className={SOURCE_BADGES[item.source_type].color}>
                      {SOURCE_BADGES[item.source_type].label}
                    </Badge>
                  )}
                </div>
                <div className="mt-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${(item.score / maxScore) * 100}%` }}
                      />
                    </div>
                    <span className={cn("text-sm font-medium w-16 text-right", colors.textSecondary)}>
                      {item.score.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};


// ==================== PARTICIPANTS VIEW ====================
const ParticipantsView = ({ spaceId, space, colors }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearingId, setClearingId] = useState(null);

  const loadParticipants = async () => {
    try {
      const res = await stackRankingApi.getParticipants(spaceId);
      setParticipants(res.data);
    } catch (err) {
      console.error('Failed to load participants:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParticipants();
  }, [spaceId]);

  const handleClearParticipant = async (participantId, name) => {
    if (!confirm(`Clear votes from ${name}? They will be able to vote again.`)) return;
    
    setClearingId(participantId);
    try {
      await stackRankingApi.clearParticipantVotes(spaceId, participantId);
      toast.success(`Cleared votes from ${name}`);
      loadParticipants();
    } catch (err) {
      toast.error('Failed to clear votes');
    } finally {
      setClearingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textAccent)} />
      </div>
    );
  }

  if (participants.length === 0) {
    return (
      <Card className={cn("p-8 text-center", colors.bgCard, colors.borderPrimary)}>
        <Users className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
        <h3 className={cn("font-semibold mb-2", colors.textPrimary)}>No participants yet</h3>
        <p className={cn("text-sm", colors.textMuted)}>
          Share the voting link to start collecting responses
        </p>
      </Card>
    );
  }

  // Calculate stats
  const completedCount = participants.filter(p => p.completion_pct >= 100).length;
  const avgCompletion = participants.length > 0 
    ? Math.round(participants.reduce((sum, p) => sum + (p.completion_pct || 0), 0) / participants.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={cn("p-4 text-center", colors.bgCard, colors.borderPrimary)}>
          <p className={cn("text-2xl font-bold", colors.textPrimary)}>{participants.length}</p>
          <p className={cn("text-sm", colors.textMuted)}>Total Participants</p>
        </Card>
        <Card className={cn("p-4 text-center", colors.bgCard, colors.borderPrimary)}>
          <p className={cn("text-2xl font-bold text-green-600")}>{completedCount}</p>
          <p className={cn("text-sm", colors.textMuted)}>Completed</p>
        </Card>
        <Card className={cn("p-4 text-center", colors.bgCard, colors.borderPrimary)}>
          <p className={cn("text-2xl font-bold", colors.textPrimary)}>{avgCompletion}%</p>
          <p className={cn("text-sm", colors.textMuted)}>Avg Completion</p>
        </Card>
      </div>

      {/* Participants List */}
      <div className="space-y-2">
        {participants.map((p) => (
          <Card key={p.participant_id} className={cn("p-4", colors.bgCard, colors.borderPrimary)}>
            <div className="flex items-center gap-4">
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium",
                p.is_guest ? "bg-gray-100 text-gray-600" : "bg-blue-100 text-blue-600"
              )}>
                {p.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className={cn("font-medium", colors.textPrimary)}>{p.name}</h4>
                  {p.is_guest ? (
                    <Badge variant="secondary" className="text-xs">Guest</Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">Team</Badge>
                  )}
                  {p.segment && (
                    <Badge variant="outline" className="text-xs">{p.segment}</Badge>
                  )}
                </div>
                <p className={cn("text-sm", colors.textMuted)}>
                  {p.email || 'No email'} • {new Date(p.voted_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Completion indicator */}
                <div className="text-right">
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          p.completion_pct >= 100 ? "bg-green-500" : "bg-blue-500"
                        )}
                        style={{ width: `${Math.min(100, p.completion_pct || 0)}%` }}
                      />
                    </div>
                    {p.completion_pct >= 100 ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <span className={cn("text-sm font-medium", colors.textSecondary)}>
                        {Math.round(p.completion_pct || 0)}%
                      </span>
                    )}
                  </div>
                  <p className={cn("text-xs mt-1", colors.textMuted)}>
                    {p.completion_label || `${p.vote_count || 0} votes`}
                  </p>
                </div>
                {/* Clear vote button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleClearParticipant(p.participant_id, p.name)}
                  disabled={clearingId === p.participant_id}
                  className="text-gray-400 hover:text-red-500"
                >
                  {clearingId === p.participant_id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default StackRankingSpace;
